import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Satellite,
  ArrowRight,
  Shield,
  Zap,
  Activity,
  MapPin,
  Play,
  Radio,
  Target,
  Network,
  Eye,
  Clock,
  Award,
  Bell,
  Map,
  TrendingUp,
  Package
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { assetManagementTestimonials } from '../../shared/testimonialData';

const AssetManagement: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Satellite,
      title: 'Satellite-Linked Tracking',
      description: 'From device provisioning and sensor telemetry to policy-based alerts and lifecycle analytics, the platform turns raw machine data into decisions that operations teams can act on. It is built for industrial environments such as utilities, tower companies, logistics fleets, government infrastructure, and insurers that need reliable visibility across thousands of remote assets.'
    },
    {
      icon: Radio,
      title: 'IoT Sensor Integration',
      description: 'A configurable policy engine watches this flow of information and automatically raises alerts, opens tickets, or dispatches field teams when thresholds are crossed. Lifecycle analytics provide a historical view of performance and degradation, helping teams plan maintenance and replacements. Satellite-linked tracking, predictive analytics, and tiered SaaS and data APIs make the solution suitable for everything from individual projects to multi-country deployments.'
    },

  ];

  const solutions = [
    {
      icon: Package,
      title: 'Energy and Utilities',
      description: 'Monitor transformers, meters, substations, and remote equipment with live telemetry so teams can spot overloads, temperature spikes, or voltage issues before they lead to outages.',
    },
    {
      icon: Activity,
      title: 'Telecom Tower Companies ',
      description: ' Track batteries, power cabinets, and network equipment across thousands of towers, using satellite-linked tracking to reduce theft, fuel pilferage, and unplanned downtime.',
    },
    {
      icon: MapPin,
      title: 'Logistics and Fleet Operations ',
      description: 'Follow high-value cargo, mobile assets, and specialized equipment in real time, combining location, condition, and usage data to improve reliability and service commitments.',
    },
    {
      icon: Target,
      title: 'Public Infrastructure and Smart Cities ',
      description: 'Give government bodies a live view of assets like streetlights, pumps, and safety equipment, moving from complaint-driven fixes to proactive, data-led maintenance.',
    },
    {
      icon: Bell,
      title: 'Insurance and Risk Programs  ',
      description: ' Provide insurers and risk managers with trusted telemetry and usage profiles so they can underwrite policies more accurately, reward good behavior, and resolve claims faster.',
    }
  ];

  const compliances = [
    {
      icon: Map,
      title: '	Secure Device Onboarding and Telemetry ',
      description: ' Every sensor and gateway is authenticated during provisioning, and all telemetry is transmitted over encrypted channels to prevent spoofing or interception.',
    },
    {
      icon: Network,
      title: 'Role-Based Access and Tenant Isolation',
      description: 'Operations teams, executives, partners, and third parties only see the assets and dashboards relevant to them, with strict separation between different business units or clients.',
    },
    {
      icon: Clock,
      title: '	Comprehensive Audit Trails',
      description: 'Configuration changes, policy updates, user actions, and field interventions are logged end-to-end, supporting internal governance, forensic analysis, and external audits.',
    },
    {
      icon: Award,
      title: '	Configurable Data Residency and Retention ',
      description: 'Data storage locations and retention periods can be tuned to align with regional regulations and corporate policies, particularly in regulated sectors such as energy and telecom.',
    },
    {
      icon: Activity,
      title: 'Integration with Enterprise Security Controls',
      description: 'The platform plugs into existing identity management, logging, and monitoring tools so that IoT data becomes part of the organization’s overall security posture, rather than sitting in a silo.',
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: '	Lower Downtime and Fewer Surprises ',
      description: 'Real-time monitoring and predictive analytics help teams intervene before assets fail, reducing emergency callouts and protecting service levels.',
    },
    {
      icon: TrendingUp,
      title: '	Longer Asset Life and Better Capex Planning ',
      description: 'Lifecycle analytics show how assets are actually used and stressed, enabling smarter decisions on refurbishment, redeployment, and replacement.',
    },
    {
      icon: Eye,
      title: '	Lean, Efficient Field Operations ',
      description: 'Automated alerts and prioritized work queues mean technicians travel with the right spares, visit the right sites, and close more jobs per shift.',
    },
    {
      icon: Zap,
      title: '	Reduced Losses from Theft and Misuse ',
      description: 'Satellite-linked tracking and anomaly detection highlight suspicious movements or consumption patterns, helping organizations protect high-value equipment.',
    },
    {
      icon: Bell,
      title:'Stronger Commercial Position  ',
      description:' Reliable performance data supports better SLAs, more attractive service offerings, and new revenue models such as usage-based contracts or data-sharing arrangements with partners.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-indigo-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 neon-glow">
                  <Satellite className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-indigo-400 uppercase tracking-wide">Satellite-Linked Tracking</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-indigo-500 to-blue-600 bg-clip-text text-transparent">Asset Management</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">IoT Asset Tracking Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
                The IoT Asset Management Platform is designed to help organizations see, control, and protect their distributed assets in real time. It connects field devices, sensors, satellite links, and cloud services into one cohesive system that shows where every asset is, how it is performing, and what needs attention. From device provisioning and sensor telemetry to policy-based alerts and lifecycle analytics, the platform turns raw machine data into decisions that operations teams can act on. It is built for industrial environments such as utilities, tower companies, logistics fleets, government infrastructure, and insurers that need reliable visibility across thousands of remote assets.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-indigo-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-500">Satellite</div>
                  <div className="text-sm text-muted-foreground">Connectivity</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">Global</div>
                  <div className="text-sm text-muted-foreground">Coverage</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Real-Time</div>
                  <div className="text-sm text-muted-foreground">Tracking</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/iot-Products/assetManagement.png"
                    alt="Asset Management IoT Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive IoT asset tracking and monitoring
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Asset Types Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Solutions </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
             
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent"> Compliance </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Enterprise-grade asset management infrastructure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-indigo-500/50 hover:border-indigo-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-indigo-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Measurable improvements in asset operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-indigo-500/50 hover:border-indigo-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-indigo-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials 
          testimonials={assetManagementTestimonials}
          title="Asset Tracking Success Stories"
          subtitle="See how organizations optimized asset management with our IoT platform"
          gradientFrom="#8A3FFC"
          gradientVia="#4F46E5"
          gradientTo="#8A3FFC"
          iconColor="#8A3FFC"
          dotColor="bg-indigo-500"
          speed={65}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-indigo-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-blue-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Track Your <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Distributed Assets?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Discover how our IoT Asset Management platform can reduce theft, improve visibility, and optimize operations across your asset portfolio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-indigo-500/30 hover:border-indigo-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AssetManagement;